import React, { useState, useEffect } from 'react'
import {
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Star,
  Clock,
  Users,
  TrendingUp,
  BookOpen,
  Sparkles,
  ChevronRight,
  Heart,
  Zap,
  GraduationCap
} from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import { api } from '../../utils/api'

interface RecommendationItem {
  id: string
  type: 'course' | 'teacher'
  score: number
  reasons: string[]
  data: any
  userFeedback?: 'interested' | 'not_interested' | 'booked' | 'ignored' | null
}

interface SmartRecommendationsProps {
  userId?: string
  showType?: 'courses' | 'teachers' | 'both'
  limit?: number
}

export const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({
  showType = 'both',
  limit = 6
}) => {
  const [courseRecommendations, setCourseRecommendations] = useState<RecommendationItem[]>([])
  const [teacherRecommendations, setTeacherRecommendations] = useState<RecommendationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feedbackStatus, setFeedbackStatus] = useState<{
    [key: string]: 'interested' | 'not_interested' | null
  }>({})

  useEffect(() => {
    fetchRecommendations()
  }, [showType, limit])

  useEffect(() => {
    const newFeedbackStatus: { [key: string]: 'interested' | 'not_interested' | null } = {}
    ;[...courseRecommendations, ...teacherRecommendations].forEach((rec) => {
      if (rec.userFeedback && ['interested', 'not_interested'].includes(rec.userFeedback)) {
        newFeedbackStatus[rec.id] = rec.userFeedback as 'interested' | 'not_interested'
      }
    })
    setFeedbackStatus(newFeedbackStatus)
  }, [courseRecommendations, teacherRecommendations])

  const fetchRecommendations = async (isRefresh = false, retryCount = 0) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const promises: Promise<any>[] = []
      const apiConfig = { timeout: 30000, retry: 2 }

      if (showType === 'courses' || showType === 'both') {
        promises.push(
          api.get(`/ai/recommendations/courses?limit=${limit}`, apiConfig).catch((err) => {
            console.warn('获取课程推荐失败:', err)
            return { data: [] }
          })
        )
      }

      if (showType === 'teachers' || showType === 'both') {
        promises.push(
          api.get(`/ai/recommendations/teachers?limit=${limit}`, apiConfig).catch((err) => {
            console.warn('获取教师推荐失败:', err)
            return { data: [] }
          })
        )
      }

      const results = await Promise.all(promises)

      if (showType === 'both') {
        setCourseRecommendations(results[0]?.data || [])
        setTeacherRecommendations(results[1]?.data || [])
      } else if (showType === 'courses') {
        setCourseRecommendations(results[0]?.data || [])
      } else {
        setTeacherRecommendations(results[0]?.data || [])
      }

      if (results.every((result) => !result?.data || result.data.length === 0)) {
        setError('暂时无法获取个性化推荐，请稍后重试')
      }
    } catch (err: any) {
      console.error('获取推荐失败:', err)
      let errorMessage = '获取推荐失败，请稍后重试'
      if (err.code === 'ECONNABORTED') {
        errorMessage = '连接超时，请检查网络后重试'
      }
      if (retryCount < 2 && err.code === 'ECONNABORTED') {
        setTimeout(() => {
          fetchRecommendations(isRefresh, retryCount + 1)
        }, (retryCount + 1) * 2000)
        return
      }
      setError(errorMessage)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleFeedback = async (
    recommendationId: string,
    feedback: 'interested' | 'not_interested'
  ) => {
    try {
      setFeedbackStatus((prev) => ({ ...prev, [recommendationId]: feedback }))
      await api.put(`/ai/recommendations/${recommendationId}/feedback`, { feedback })

      if (feedback === 'interested') {
        const recommendation = [...courseRecommendations, ...teacherRecommendations].find(
          (item) => item.id === recommendationId
        )
        if (recommendation) {
          const interestContext: any = {}
          if (recommendation.type === 'course') {
            interestContext.subject = recommendation.data.subject
            await api.post('/ai/interests', {
              type: 'subject',
              content: recommendation.data.subject,
              context: interestContext
            })
          }
        }
      } else {
        setTimeout(() => {
          setCourseRecommendations((prev) => prev.filter((item) => item.id !== recommendationId))
          setTeacherRecommendations((prev) => prev.filter((item) => item.id !== recommendationId))
        }, 800)
      }
    } catch (err) {
      console.error('提交反馈失败:', err)
      setFeedbackStatus((prev) => ({ ...prev, [recommendationId]: null }))
    }
  }

  const handleRefresh = () => {
    fetchRecommendations(true)
  }

  // 课程卡片组件
  const CourseCard: React.FC<{ recommendation: RecommendationItem }> = ({ recommendation }) => {
    const course = recommendation.data
    const matchScore = Math.round(recommendation.score * 100)

    return (
      <div className="group bg-white rounded-2xl border border-slate-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-indigo-100/50 hover:border-indigo-200 hover:-translate-y-1">
        {/* 顶部渐变装饰 */}
        <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

        <div className="p-6">
          {/* 头部标签 */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-indigo-600" />
              </div>
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">
                AI 推荐
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-full border border-emerald-200">
              <Zap className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">{matchScore}% 匹配</span>
            </div>
          </div>

          {/* 课程信息 */}
          <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors line-clamp-1">
            {course.title}
          </h3>
          <p className="text-sm text-slate-500 mb-4 line-clamp-2">{course.description}</p>

          {/* 教师和评分 */}
          <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {course.teacher?.user?.firstName?.[0]}
              </div>
              <div>
                <div className="text-sm font-medium text-slate-800">
                  {course.teacher?.user?.firstName} {course.teacher?.user?.lastName}
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                  <span className="text-xs text-slate-500">{course.teacher?.rating || 0}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-indigo-600">¥{course.finalPricePerHour}</div>
              <div className="text-xs text-slate-400">/小时</div>
            </div>
          </div>

          {/* 课程信息标签 */}
          <div className="flex items-center gap-3 mb-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {course.duration}小时
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {course.studentCount || 0}人学习
            </span>
          </div>

          {/* 推荐理由 */}
          <div className="mb-5">
            <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
              <TrendingUp className="h-3.5 w-3.5" />
              推荐理由
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recommendation.reasons.slice(0, 2).map((reason, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100"
                >
                  {reason}
                </span>
              ))}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2">
            {feedbackStatus[recommendation.id] === 'interested' ? (
              <div className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 rounded-xl border border-emerald-200">
                <Heart className="h-4 w-4 fill-emerald-500" />
                <span className="text-sm font-semibold">已记录兴趣</span>
              </div>
            ) : feedbackStatus[recommendation.id] === 'not_interested' ? (
              <div className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-slate-100 text-slate-500 rounded-xl">
                <span className="text-sm">即将移除...</span>
              </div>
            ) : (
              <>
                <button
                  onClick={() => handleFeedback(recommendation.id, 'interested')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold text-sm shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50 transition-all"
                >
                  <ThumbsUp className="h-4 w-4" />
                  感兴趣
                </button>
                <button
                  onClick={() => handleFeedback(recommendation.id, 'not_interested')}
                  className="p-3 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                  title="不感兴趣"
                >
                  <ThumbsDown className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // 教师卡片组件
  const TeacherCard: React.FC<{ recommendation: RecommendationItem }> = ({ recommendation }) => {
    const teacher = recommendation.data
    const matchScore = Math.round(recommendation.score * 100)

    return (
      <div className="group bg-white rounded-2xl border border-slate-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-purple-100/50 hover:border-purple-200 hover:-translate-y-1">
        {/* 顶部渐变装饰 */}
        <div className="h-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500" />

        <div className="p-6">
          {/* 头部标签 */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="h-4 w-4 text-purple-600" />
              </div>
              <span className="text-xs font-bold text-purple-600 uppercase tracking-wider">
                名师推荐
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-full border border-emerald-200">
              <Zap className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">{matchScore}% 匹配</span>
            </div>
          </div>

          {/* 教师信息 */}
          <div className="flex items-start gap-4 mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {teacher.user?.avatar ? (
                  <img
                    src={teacher.user.avatar}
                    alt=""
                    className="w-full h-full rounded-2xl object-cover"
                  />
                ) : (
                  <>
                    {teacher.user?.firstName?.[0]}
                    {teacher.user?.lastName?.[0]}
                  </>
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                <span className="text-[8px] text-white font-bold">✓</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900 group-hover:text-purple-600 transition-colors">
                {teacher.user?.firstName} {teacher.user?.lastName}
              </h3>
              <p className="text-sm text-slate-500">{teacher.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3.5 w-3.5 ${
                        i < Math.floor(teacher.rating || 0)
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs text-slate-500">
                  ({teacher.bookings?.length || 0}条评价)
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-slate-500 mb-4 line-clamp-2">{teacher.introduction}</p>

          {/* 专业领域 */}
          {teacher.specialties && teacher.specialties.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-slate-500 mb-2">专业领域</div>
              <div className="flex flex-wrap gap-1.5">
                {teacher.specialties.slice(0, 3).map((specialty: string, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 推荐理由 */}
          <div className="mb-5">
            <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
              <TrendingUp className="h-3.5 w-3.5" />
              推荐理由
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recommendation.reasons.slice(0, 2).map((reason, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100"
                >
                  {reason}
                </span>
              ))}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2">
            {feedbackStatus[recommendation.id] === 'interested' ? (
              <div className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 rounded-xl border border-emerald-200">
                <Heart className="h-4 w-4 fill-emerald-500" />
                <span className="text-sm font-semibold">已记录兴趣</span>
              </div>
            ) : feedbackStatus[recommendation.id] === 'not_interested' ? (
              <div className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-slate-100 text-slate-500 rounded-xl">
                <span className="text-sm">即将移除...</span>
              </div>
            ) : (
              <>
                <button
                  onClick={() => handleFeedback(recommendation.id, 'interested')}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-semibold text-sm shadow-lg shadow-purple-200/50 hover:shadow-xl hover:shadow-purple-300/50 transition-all"
                >
                  <ThumbsUp className="h-4 w-4" />
                  感兴趣
                </button>
                <button
                  onClick={() => handleFeedback(recommendation.id, 'not_interested')}
                  className="p-3 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                  title="不感兴趣"
                >
                  <ThumbsDown className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative mb-6">
          <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
          <Sparkles className="w-6 h-6 text-indigo-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-slate-600 font-medium">AI 正在为您寻找最佳推荐...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-16 px-6">
        <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-10 h-10 text-rose-400" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">获取推荐失败</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button
          onClick={() => fetchRecommendations()}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl transition-all"
        >
          <RefreshCw className="h-4 w-4" />
          重新加载
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      {/* 头部 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">AI 智能推荐</h2>
          </div>
          <p className="text-slate-500">根据您的偏好和学习历史，为您推荐最适合的内容</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? '刷新中...' : '换一批'}
        </button>
      </div>

      {/* 课程推荐 */}
      {(showType === 'courses' || showType === 'both') && courseRecommendations.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">推荐课程</h3>
              <p className="text-sm text-slate-500">为你精选的优质课程</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseRecommendations.map((recommendation) => (
              <CourseCard key={recommendation.id} recommendation={recommendation} />
            ))}
          </div>
        </div>
      )}

      {/* 教师推荐 */}
      {(showType === 'teachers' || showType === 'both') && teacherRecommendations.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">推荐教师</h3>
              <p className="text-sm text-slate-500">与你最匹配的名师</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teacherRecommendations.map((recommendation) => (
              <TeacherCard key={recommendation.id} recommendation={recommendation} />
            ))}
          </div>
        </div>
      )}

      {/* 空状态 */}
      {courseRecommendations.length === 0 && teacherRecommendations.length === 0 && (
        <div className="text-center py-16 px-6">
          <div className="relative inline-flex mb-6">
            <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-xl animate-pulse" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-full flex items-center justify-center border border-indigo-100">
              <BookOpen className="w-10 h-10 text-indigo-400" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">暂无推荐内容</h3>
          <p className="text-slate-500 mb-8 max-w-md mx-auto">
            完善您的学习偏好设置，我们将为您提供更精准的个性化推荐
          </p>
          <button
            onClick={() => (window.location.href = '/ai/preferences')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl transition-all"
          >
            <Sparkles className="h-4 w-4" />
            设置偏好
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
